import { CapabilitySet, KnownCapability, ScopeLevel } from "@/types/capability.types";

import { hasAnyCapability } from "./hasCapability";

/**
 * Report-read keys accepted per context level: the level's own report key plus
 * its strict ancestors' keys. The backend resolver's ancestor walk contributes
 * each ancestor node's exact keys into a descendant's capability set, so a
 * platform-level grant appears verbatim in org/league sets — the FE only needs
 * to accept it, not re-derive the hierarchy (same justification as
 * `AUDIT_READ_KEYS` / `FINANCE_READ_KEYS`). The dashboard home renders at
 * platform/organization level; the league entry mirrors the organization one
 * for completeness.
 *
 * The two arms deliberately use different key families (DEV-4502). The backend
 * retired `PLATFORM_REPORT:READ` — it is stripped from every role bundle and
 * direct grant, so nothing can hold it after the migration — and the product
 * decision was to repoint platform-level report-read at `PLATFORM_FINANCE:READ`
 * rather than drop the gate. `ORG_REPORT:READ` is explicitly staying on the
 * backend, so the organization/league arms keep their own report key; only the
 * retired platform ancestor entry moves to the finance key. Consequence, and it
 * is intended: `PLATFORM_FINANCE:READ` is a narrower grant than the old
 * report-read, so platform-scoped roles without finance-read no longer reach the
 * dashboard home. Do not add a fallback to restore the old breadth.
 */
export const REPORT_READ_KEYS = {
  platform: ["PLATFORM_FINANCE:READ"],
  organization: ["ORG_REPORT:READ", "PLATFORM_FINANCE:READ"],
  league: ["ORG_REPORT:READ", "PLATFORM_FINANCE:READ"],
} as const satisfies Record<ScopeLevel, readonly KnownCapability[]>;

/**
 * Whether the set grants report/dashboard read at the given context level (own
 * key or an ancestor level's key).
 */
export const hasReportRead = (set: CapabilitySet | null | undefined, level: ScopeLevel): boolean =>
  hasAnyCapability(set, REPORT_READ_KEYS[level]);
